<template>
  <div>
    <CCard class="bg-style2 mt-3">
      <CCardHeader
          class="bg-gradient-dark text-white"
          style="border-top-left-radius: 1rem; border-top-right-radius: 1rem;"
      >
        <span class="font-weight-bold h6">
          <CIcon v-if="icon" :name="icon" /> {{ caption }}
        </span>
        <div class="card-header-actions">Total: {{ setters.length }}</div>
      </CCardHeader>

      <CCardBody>
        <CRow>
          <CCol>
            <CDataTable
                :hover="hover"
                :striped="striped"
                :bordered="bordered"
                :small="small"
                :fixed="fixed"
                :dark="dark"
                :loading="loading"
                :items="setters"
                :fields="fields"
                :items-per-page="10"
                pagination
            >
              <template #index="{ index }">
                <td class="text-center">{{ index + 1 }}</td>
              </template>

              <template #eventName="{ item }">
                <td>{{ item.eventName }}</td>
              </template>

              <template #typeName="{ item }">
                <td>{{ item.typeName }}</td>
              </template>

              <template #startDate="{ item }">
                <td>{{ item.startDate }}</td>
              </template>

              <template #endDate="{ item }">
                <td>{{ item.endDate }}</td>
              </template>

              <template #action="{ item }">
                <td>
                  <CButton
                      class="mr-1"
                      size="sm"
                      color="warning"
                      shape="pill"
                      variant="outline"
                      @click="onEditTime(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                      size="sm"
                      color="danger"
                      shape="pill"
                      variant="outline"
                      @click="onDelete(item)"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import api from '@/service/api.js'
import Swal from 'sweetalert2'
import moment from 'moment-timezone'
import { CIcon } from '@coreui/vue'
import '@/assets/css/swal.css'

export default {
  name: 'SetterList',
  components: { CIcon },

  props: {
    icon:    { type: String, default: 'cil-clock' },
    caption: { type: String, default: 'TIME SETTINGS' },
    hover:   Boolean,
    striped: Boolean,
    bordered:Boolean,
    small:   Boolean,
    fixed:   Boolean,
    dark:    Boolean,
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      setters: [],
      fields: [
        { key: 'index',     label: 'No.',            _style: 'width:60px; text-align:center' },
        { key: 'eventName', label: 'Event' },
        { key: 'typeName',  label: 'Candidate Type' },
        { key: 'startDate', label: 'Start Date' },
        { key: 'endDate',   label: 'End Date' },
        { key: 'action',    label: 'Actions',        _style: 'width:120px' },
      ],
    }
  },

  methods: {
    async onInit() {
      try {
        const res = await api.setter('get')
        const data = res?.data?.data || []
        this.setters = data.map(s => ({
          _id: s._id,
          eventName: s.title?.event_name || s.title,
          typeName: s.type?.type_name || s.type,
          startDateRaw: moment(s.startDate).tz("Asia/Bangkok").format("YYYY-MM-DDTHH:mm"), // for popup
          endDateRaw: moment(s.endDate).tz("Asia/Bangkok").format("YYYY-MM-DDTHH:mm"),
          startDate: moment(s.startDate).tz("Asia/Bangkok").format("MM/DD/YYYY HH:mm"), // for table
          endDate: moment(s.endDate).tz("Asia/Bangkok").format("MM/DD/YYYY HH:mm"),
        }))
      } catch (e) {
        console.error('Error fetching setters:', e)
      }
    },

    async onEditTime(item) {
      const now = moment().tz("Asia/Bangkok").format("MM/DD/YYYY HH:mm")

      const { value: formValues } = await Swal.fire({
        title: 'Edit Registration Window',
        html: `
  <div class="swal-form">
    <div class="swal-form-row">
      <label>Event</label>
      <input type="text" value="${item.eventName}" disabled>
    </div>
    <div class="swal-form-row">
      <label>Candidate Type</label>
      <input type="text" value="${item.typeName}" disabled>
    </div>
    <div class="swal-form-row">
      <label>Start Date</label>
      <input type="datetime-local" id="swal-start"
             value="${item.startDateRaw}"
             min="${now}">
    </div>
    <div class="swal-form-row">
      <label>End Date</label>
      <input type="datetime-local" id="swal-end"
             value="${item.endDateRaw}"
             min="${now}">
    </div>
  </div>
`
        ,
        customClass: {
          confirmButton: 'swal2-confirm btn btn-success btn-sm',
          cancelButton: 'swal2-cancel btn btn-secondary btn-sm'
        },
        showCancelButton: true,
        focusConfirm: false,
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
        preConfirm: () => {
          const start = document.getElementById('swal-start').value
          const end = document.getElementById('swal-end').value
          if (!start || !end) {
            Swal.showValidationMessage('Both start and end dates are required')
          }
          if (start >= end) {
            Swal.showValidationMessage('End date must be after start date')
          }
          return { startDate: start, endDate: end }
        }
      })

      if (!formValues) return

      const { startDate, endDate } = formValues

      try {
        await api.setter('put', {
          _id: item._id,
          startDate,
          endDate
        })
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Registration window times updated successfully.',
          timer: 1800,
          showConfirmButton: false
        })
        this.onInit()
      } catch (err) {
        console.error('Failed to update time:', err)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.error || err.message
        })
      }
    },

    async onDelete(item) {
      const result = await Swal.fire({
        title: `Delete Registration Window?`,
        html: `
          <div style="text-align: left; font-size: 0.9rem;">
            <p><strong>Event:</strong> ${item.eventName}</p>
            <p><strong>Candidate Type:</strong> ${item.typeName}</p>
            <p><strong>Start:</strong> ${item.startDate}</p>
            <p><strong>End:</strong> ${item.endDate}</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-danger btn-sm',
          cancelButton: 'btn btn-secondary btn-sm ml-2'
        },
        buttonsStyling: false
      })

      if (!result.isConfirmed) return

      try {
        const res = await api.setter('delete', { id: item._id })
        const mongoResult = res.data.data
        if (mongoResult.deletedCount === 1 || mongoResult.n === 1) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted',
            text: 'The registration window was removed.',
            timer: 1800,
            showConfirmButton: false
          })
          this.onInit()
        } else {
          throw new Error('No document was deleted')
        }
      } catch (err) {
        console.error('Delete failed:', err.response?.data || err.message)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.error || err.message
        })
      }
    }
  },

  created() {
    this.onInit()
  }
}
</script>

<style scoped>
.text-center p {
  font-style: italic;
}
</style>
